// Development only (DEV_TOOLS=1): make the current decision for whoever's turn it is, to try out
// the flow of a game from one browser. The server does not offer these routes in production.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t, errorText } from '../i18n/index.ts';
import { api, newKey } from '../lib/api.ts';
import { PlayingCard, useToast } from '../components/ui.tsx';
import type { LegalActions } from '../../server/engine/hand.ts';
import type { Card } from '../../shared/cards.ts';
import type { AmountFormatter } from '../lib/amounts.ts';

interface DevTurn {
  userId: string;
  displayName: string;
  turnId: string;
  hole: Card[] | null;
  legal: LegalActions;
}

export function useDevTools(): boolean {
  const q = useQuery({
    queryKey: ['dev-status'],
    queryFn: () => api.get<{ enabled: boolean }>('/api/dev/status'),
    retry: false,
    staleTime: Infinity,
  });
  return q.data?.enabled === true;
}

export function DevActPanel({ tournamentId, turnKey, fmt }: { tournamentId: string; turnKey: string; fmt: AmountFormatter }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['dev-turn', tournamentId, turnKey],
    queryFn: () => api.get<{ turn: DevTurn | null }>(`/api/dev/tournaments/${tournamentId}/turn`),
  });
  const act = useMutation({
    mutationFn: (a: { type: string; amount?: number }) =>
      api.post(`/api/dev/tournaments/${tournamentId}/act`, { turnId: q.data!.turn!.turnId, ...a, idempotencyKey: newKey() }),
    onError: (err) => toast(errorText(err), 'error'),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['table', tournamentId] }),
  });
  const turn = q.data?.turn;
  if (!turn) return null;
  const { legal } = turn;
  const range = legal.bet ?? legal.raise;
  const kind = legal.bet ? 'bet' : 'raise';
  return (
    <section className="dev-panel" aria-label={t('dev.title')}>
      <div className="dev-head">
        <strong>🛠 {t('dev.title')}</strong>
        <span>{t('dev.actingFor', { name: turn.displayName })}</span>
        {turn.hole && (
          <span className="cards cards-tight">
            {turn.hole.map((c) => (
              <PlayingCard key={c} card={c} size="s" />
            ))}
          </span>
        )}
      </div>
      <div className="btn-row">
        {legal.canFold && (
          <button className="btn btn-small" disabled={act.isPending} onClick={() => act.mutate({ type: 'fold' })}>
            Fold
          </button>
        )}
        {legal.canCheck && (
          <button className="btn btn-small" disabled={act.isPending} onClick={() => act.mutate({ type: 'check' })}>
            Check
          </button>
        )}
        {legal.call && (
          <button className="btn btn-small" disabled={act.isPending} onClick={() => act.mutate({ type: 'call' })}>
            Call {fmt(legal.call.amount)}
          </button>
        )}
        {range && (
          <button className="btn btn-small" disabled={act.isPending} onClick={() => act.mutate({ type: kind, amount: range.min })}>
            {kind === 'bet' ? 'Bet' : 'Raise'} {fmt(range.min)}
          </button>
        )}
        {range && range.max > range.min && (
          <button className="btn btn-small" disabled={act.isPending} onClick={() => act.mutate({ type: kind, amount: range.max })}>
            All-in {fmt(range.max)}
          </button>
        )}
      </div>
      <p className="hint">{t('dev.note')}</p>
    </section>
  );
}
